import { DecimalPipe } from '@angular/common';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import writeXlsxFile, { type CellObject, type SheetData } from 'write-excel-file/browser';
import { IRPFConceptType } from 'src/app/models/IRPFCategory';
import { IRPFConceptRow } from 'src/app/models/IRPFConceptRow';
import { IRPFInformation } from 'src/app/models/IRPFInformation';

@Injectable({
    providedIn: 'root'
})
export class IrpfTableService {
    private readonly translateService = inject(TranslateService);
    private readonly worksheetName = 'IRPF';
    private readonly decimalPipe = inject(DecimalPipe);

    queryId = '';
    irpfInfo = {} as IRPFInformation;
    rows: WritableSignal<IRPFConceptRow[]> = signal([]);
    isInit = signal(false);

    init(queryId: string, irpfInfo: IRPFInformation): void {
        this.queryId = queryId;
        this.irpfInfo = irpfInfo;
        this.rows.set(irpfInfo.categories.flatMap(category => {
            const headerRow: IRPFConceptRow = { isCategoryHeader: true, category: category.label };
            const conceptRows = category.concepts.map(concept => {
                const row: IRPFConceptRow = {
                    concept: concept.label,
                    ...Object.keys(IRPFConceptType)
                        .filter(k => isNaN(Number(k)))
                        .reduce((acc, key) => {
                            const value = concept.valuePerIRPFType[key as keyof typeof IRPFConceptType];
                            acc[key] = value;
                            return acc;
                        }, {} as any),
                    isSingleRow: concept.isSingleRow
                };
                return row;
            });
            return [headerRow, ...conceptRows];
        }));
        this.isInit.set(true);
    }

    reset(): void {
        this.queryId = '';
        this.irpfInfo = {} as IRPFInformation;
        this.rows.set([]);
        this.isInit.set(false);
    }

    getSingleRowFormattedValue(row: IRPFConceptRow): string {
        const value = this.getSingleRowValue(row);
        if (value === null) return 'N/A';
        return this.decimalPipe.transform(value, '1.0-2', 'es-ES') ?? 'N/A';
    }

    private getSingleRowValue(row: IRPFConceptRow): string | number | null {
        const value = row.DirectEstimation ?? row.SimplifiedDirectEstimation ?? row.ObjectiveEstimation ?? row.ObjectiveEstimationAgriculturalLivestockForestry ?? null;
        return value;
    }

    // #region Excel
    generateExcelBlob(): Promise<Blob> {
        return writeXlsxFile(this.getSheetData(), {
            sheet: this.worksheetName,
            columns: [
                { width: 85 },
                { width: 15 },
                { width: 15 },
                { width: 15 },
                { width: 15 }
            ]
        }).toBlob();
    }

    private getSheetData(): SheetData {
        const data: SheetData = [
            [
                undefined,
                this.cell(this.formatIrpfTypeCellText(), {
                    columnSpan: 4,
                    align: 'center',
                    alignVertical: 'center',
                    wrap: true,
                    height: this.irpfInfo.isKnownEstimation ? 45 : 100
                })
            ],
            [
                this.cell(this.translateService.instant('IRPF.HEADERS.CONCEPT'), { fontWeight: 'bold', align: 'center', alignVertical: 'center', wrap: true }),
                this.cell(this.translateService.instant('IRPF.HEADERS.DIRECT_ESTIMATION'), { fontWeight: 'bold', align: 'center', alignVertical: 'center', wrap: true }),
                this.cell(this.translateService.instant('IRPF.HEADERS.SIMPLIFIED_DIRECT_ESTIMATION'), { fontWeight: 'bold', align: 'center', alignVertical: 'center', wrap: true }),
                this.cell(this.translateService.instant('IRPF.HEADERS.OBJECTIVE_ESTIMATION'), { fontWeight: 'bold', align: 'center', alignVertical: 'center', wrap: true }),
                this.cell(this.translateService.instant('IRPF.HEADERS.OBJECTIVE_ESTIMATION_AGRICULTURAL_LIVESTOCK_FORESTRY'), { fontWeight: 'bold', align: 'center', alignVertical: 'center', wrap: true })
            ]
        ];

        this.rows().forEach(row => {
            if (row.isCategoryHeader) {
                data.push([this.cell(this.translateService.instant(row.category), {
                    columnSpan: 5,
                    fontWeight: 'bold',
                    backgroundColor: '#EFEFEF',
                    align: 'left',
                    alignVertical: 'center',
                    wrap: true,
                    height: 25
                })]);
            } else {
                if (row.isSingleRow) {
                    data.push([
                        this.cell(this.translateService.instant(row.concept), { alignVertical: 'center', wrap: true, height: this.isRentsRow(row.concept) ? 100 : 45 }),
                        this.cell(this.getSingleRowValue(row), { columnSpan: 4, align: 'center', alignVertical: 'center', wrap: true, height: this.isRentsRow(row.concept) ? 100 : 45 })
                    ]);
                } else {
                    const height = this.isRentsRow(row.concept) ? 100 : 45;
                    data.push([
                        this.cell(this.formatConceptCellText(row.concept), { alignVertical: 'center', wrap: true, height }),
                        this.cell(row.DirectEstimation ?? 'N/A', { align: 'center', alignVertical: 'center', wrap: true, height }),
                        this.cell(row.SimplifiedDirectEstimation ?? 'N/A', { align: 'center', alignVertical: 'center', wrap: true, height }),
                        this.cell(row.ObjectiveEstimation ?? 'N/A', { align: 'center', alignVertical: 'center', wrap: true, height }),
                        this.cell(row.ObjectiveEstimationAgriculturalLivestockForestry ?? 'N/A', { align: 'center', alignVertical: 'center', wrap: true, height })
                    ]);
                }
            }
        });

        return data;
    }

    private formatIrpfTypeCellText(): string {
        const title = this.translateService.instant('IRPF.HEADERS.IRPF_TYPE');

        if (!this.irpfInfo.isKnownEstimation) {
            return `${title}\n\n${this.translateService.instant('IRPF.INFORMATION.UNKNOWN_ESTIMATION')}`;
        }

        return title;
    }

    private formatConceptCellText(concept: string): string {
        const translatedConcept = this.translateService.instant(concept);
        if (this.isRentsRow(concept)){
            return `${translatedConcept}\n\n${this.translateService.instant('IRPF.INFORMATION.RENTS')}`;
        }

        return translatedConcept;
    }

    private cell(value: any, style: Partial<CellObject> = {}): CellObject {
        return { value, ...style };
    }

    private isRentsRow(concept: string): boolean {
        return concept === 'IRPF.INCOME_AND_EXPENSES_FROM_BUSINESS_ACTIVITIES.RENTS';
    }
    // #endregion
}
